/**
 * Tailwind chrome for the ICS-204 assignment table / edit form.
 *
 * The table wrapper carries the `dark` class so shadcn's dark token set
 * resolves (see `.dark` in src/styles/tailwind.css); without it the primitives
 * fall back to the light `:root` tokens and render white on the dark IAP
 * panel.
 *
 * Surfaces use jet black so the list reads as a modern dark document panel.
 */

/** Shared ICS-204 panel fill (header, content, table, cards, dialogs). */
export const ICS204_SURFACE = 'tw:bg-black';

/** Edit-mode form header row (assignee/status selects). */
export const ICS204_HEADER_ROW = `tw:grid tw:cursor-pointer tw:items-center ${ICS204_SURFACE} tw:p-3 tw:hover:bg-black tw:active:bg-black`;

/**
 * Grid template for the edit-mode form header.
 *
 * For the usual Assignee + Status summary (2 columns): chevron, flexible
 * Assignee, fixed Status.
 */
export const ics204SummaryGridTemplate = (columnCount: number): string => {
  if (columnCount === 2) {
    return '2rem minmax(0, 1fr) 12rem';
  }
  return `2rem ${Array.from(
    { length: columnCount },
    () => 'minmax(0, 1fr)'
  ).join(' ')}`;
};

/** Replaces `.filter-card-content`. */
export const ICS204_CONTENT = `${ICS204_SURFACE} tw:p-2`;

/** Replaces `.filter-card-content-grid`. */
export const ICS204_CONTENT_GRID =
  'tw:mb-4 tw:grid tw:grid-cols-[25%_25%_22%_25%] tw:gap-4';

/** One numbered box from the ICS 204-CG form. */
export const ICS204_SECTION =
  'tw:rounded tw:border tw:border-[var(--border-primary)] tw:p-3';

export const ICS204_SECTION_TITLE = 'tw:mb-3 tw:font-semibold';

/**
 * Bordered block nested inside a section (work assignment, special
 * instructions).
 */
export const ICS204_SUBCARD =
  'tw:rounded tw:border tw:border-[var(--border-primary)] tw:p-2';

/**
 * Label and secondary text. Uses the DICE token rather than
 * `tw:text-muted-foreground` so it is tuned to the panel the card sits on
 * instead of shadcn's darker `--background`.
 */
export const ICS204_LABEL = 'tw:text-xs tw:text-[var(--text-weak)]';

export const ICS204_MUTED = 'tw:text-[var(--text-weak)]';

export const ICS204_ICON_BTN =
  'tw:text-[var(--text-white)]! tw:hover:bg-[var(--neutral-900)]';

export const ICS204_OUTLINE_BTN =
  'tw:border-[var(--border-primary)] tw:bg-black tw:text-[var(--text-white)] tw:hover:bg-[var(--neutral-900)]';

/** Destructive icon action, e.g. removing an editable row. */
export const ICS204_DELETE_BTN =
  'tw:text-[var(--text-white)]! tw:hover:bg-[var(--error-600)]';

/**
 * Every `Table` inside the card. Its wrapper div hardcodes `tw:bg-background`,
 * which under `dark` is shadcn's near-black and reads as a black box inside the
 * section; `className` lands on the `<table>`, which is `w-full` inside an
 * unpadded wrapper and so paints over it. Row hover lifts to `--neutral-900`
 * against jet black. Done here rather than in components/ui/table.tsx, which
 * the ui/ README asks to keep recognizable as shadcn.
 */
export const ICS204_TABLE = `${ICS204_SURFACE} tw:[&_tbody_tr:hover]:bg-[var(--neutral-900)]`;

/** Field grid inside an editable sub-card. */
export const ICS204_EDIT_GRID =
  'tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2';

/** TipTap edit surface on the ICS-204 card (jet black + focus ring). */
export const ICS204_TIPTAP = 'tiptap-editor--ics204';

/** Inline caveat explaining a cross-form side effect of an edit. */
export const ICS204_HINT = 'tw:text-xs tw:text-[var(--text-weak)] tw:italic';

/**
 * Any dialog a card opens.
 *
 * Dialogs portal out of the card, so they carry `dark` themselves: the variant
 * is class-scoped and the portal root is outside the card's own `dark`
 * subtree.
 */
export const DIALOG_SURFACE =
  'dark tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';
